"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { Star, Quote, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
const testimonials = [{
  id: 1,
  name: "Sarah Jenkins",
  role: "Homeowner",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/testimonial_sarah.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/testimonial_sarah.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/testimonial_sarah.webp-wvc-sizes",
    alt: "Portrait of Sarah Jenkins"
  },
  quote: "A preventative maintenance check caught a worn HVAC component before summer arrived. The pro explained options clearly and provided a straightforward quote.",
  highlight: "Preventative Care"
}, {
  id: 2,
  name: "Michael Chen",
  role: "Property Manager",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/testimonial_michael.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/testimonial_michael.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/testimonial_michael.webp-wvc-sizes",
    alt: "Portrait of Michael Chen"
  },
  quote: "Clear communication and thorough work. The pro arrived on time, explained everything clearly, and left the work area clean.",
  highlight: "Clear Communication"
}, {
  id: 3,
  name: "Emily Rodriguez",
  role: "Small Business Owner",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/testimonial_emily.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/testimonial_emily.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/testimonial_emily.webp-wvc-sizes",
    alt: "Portrait of Emily Rodriguez"
  },
  quote: "Setting up a seasonal maintenance schedule was easy. The pro gave a quote before work started and took care of our routine checklist.",
  highlight: "Straightforward Service"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};
export default function MaintenanceTestimonials() {
  return <section id="maintenancetestimonials" className="bg-background py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.5
        }} transition={{
          duration: 0.5
        }}>
            <Badge variant="secondary" className="mb-4">
              <CheckCircle2 className="w-4 h-4 mr-1 text-primary" />
              Verified Reviews
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
              Trusted by Homeowners & Businesses
            </h2>
            <p className="text-lg text-muted-foreground font-sans">
              See how regular maintenance helps homeowners protect their property and plan upkeep smoothly.
            </p>
          </motion.div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, i) => <motion.div key={testimonial.id} variants={itemVariants} data-index={i}>
              <Card className="h-full flex flex-col bg-card border-border shadow-md hover:shadow-lg transition-shadow duration-300 relative">
                <div className="absolute top-6 right-6 text-primary/20">
                  <Quote size={40} />
                </div>
                <CardHeader className="pb-4">
                  <Badge variant="outline" className="w-fit mb-4 bg-background/50">
                    {testimonial.highlight}
                  </Badge>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-card-foreground font-sans text-base leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                </CardContent>
                <CardFooter className="pt-6 border-t border-border/50 mt-auto">
                  <div className="flex items-center gap-4">
                    <SafeHydrate><Avatar className="w-12 h-12 border-2 border-background shadow-sm">
                      <AvatarImage src={testimonial.image.src} data-wvc-srcset={testimonial.image.srcset} data-wvc-sizes={testimonial.image.sizes} alt={testimonial.image.alt} />
                      <AvatarFallback className="font-default font-semibold text-primary">
                        {testimonial.name.split(" ").map(n => n[0]).join("")}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <h3 className="font-default font-semibold text-foreground text-sm">
                        {testimonial.name}
                      </h3>
                      <p className="text-xs text-muted-foreground font-sans">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}